// components/LectorDNI.js
import React, { useState, useRef, useEffect } from 'react';
import {
  Modal,
  Button,
  Space,
  message,
  Tag,
  Card,
  Descriptions,
  Spin,
  Alert,
  Typography,
  Tabs,
  Upload,
  Image
} from 'antd';
import {
  CameraOutlined,
  UploadOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
  IdcardOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  TeamOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { Html5QrcodeScanner } from 'html5-qrcode';

import { BASE_URL } from '../utils/constants';

const { Text, Title } = Typography;
const { TabPane } = Tabs;

const LectorDNI = ({ 
  visible, 
  onClose, 
  onSuccess,
  numReserva,
  linea,
  numOcupante,
  nombreCliente
}) => {
  const [modo, setModo] = useState('fisico'); // 'fisico' o 'digital'
  const [loading, setLoading] = useState(false);
  const [documentoData, setDocumentoData] = useState(null);
  const [error, setError] = useState(null);
  const [needsNextPage, setNeedsNextPage] = useState(false);
  const [currentDocumentId, setCurrentDocumentId] = useState(null);
  const [frontImage, setFrontImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  const [qrScanning, setQrScanning] = useState(false);
  const scannerRef = useRef(null);  
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // Iniciar cámara
  const startCamera = async () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Error accediendo a la cámara:', err);
      message.error('No se pudo acceder a la cámara. Verifica los permisos.');
    }
  };

  const iniciarEscaneoQR = () => {
    setQrScanning(true);
    // El scanner se iniciará en el useEffect cuando el div esté listo
  };  

useEffect(() => {
  if (modo === 'digital' && qrScanning && !documentoData) {
    const scanner = new Html5QrcodeScanner(
      "qr-reader",
      { fps: 10, qrbox: { width: 250, height: 250 } },
      false
    );
    
    scanner.render(success, error);
    
    function success(decodedText) {
      scanner.clear();
      setQrScanning(false);
      // decodedText contiene los datos del QR (normalmente JSON o texto plano)
      procesarQR(decodedText);
    }
    
    function error(err) {
      console.warn(err);
    }
    
    return () => {
      scanner.clear();
    };
  }
}, [modo, qrScanning]);

const procesarQR = async (qrData) => {
  setLoading(true);
  try {
    // Envías el texto del QR a tu backend
    const response = await axios.post(`${BASE_URL}/api/dni/procesar-qr`, {
      qrData: qrData,
      numReserva, linea, numOcupante
    });
    // ... manejar respuesta
  } catch (error) {
    message.error('Error al procesar QR');
  }
  setLoading(false);
};

  // Capturar foto desde cámara
  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext('2d');
      canvasRef.current.width = videoRef.current.videoWidth;
      canvasRef.current.height = videoRef.current.videoHeight;
      context.drawImage(videoRef.current, 0, 0);
      
      canvasRef.current.toBlob(async (blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          setFrontImage(url);
          setPreviewImage(url);
          
          if (!needsNextPage) {
            await procesarDocumento(blob);
          } else {
            await procesarReverso(blob);
          }
        }
      }, 'image/jpeg', 0.9);
    }
  };

  // Procesar documento (primera cara)
  const procesarDocumento = async (imageBlob) => {
    setLoading(true);
    setError(null);
    
    const formData = new FormData();
    formData.append('image', imageBlob);
    formData.append('numReserva', numReserva);
    formData.append('linea', linea);
    formData.append('numOcupante', numOcupante);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BASE_URL}/api/dni_delta/procesar`,
        formData,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const result = response.data;

      if (result.needsNextPage) {
        setNeedsNextPage(true);
        setCurrentDocumentId(result.documentId);
        message.info('Por favor, escanea el reverso del DNI');
      } else if (result.success) {
        setDocumentoData(result.data);
        message.success('Documento procesado correctamente');
        
        // Si hay callback de éxito
        if (onSuccess) {
          onSuccess({
            ...result.data,
            numReserva,
            linea,
            numOcupante
          });
        }
      } else {
        setError(result.error || 'Error al procesar el documento');
      }
    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.error || 'Error de conexión con el servidor');
    } finally {
      setLoading(false);
    }
  };

  // Procesar reverso
  const procesarReverso = async (imageBlob) => {
    setLoading(true);
    
    const formData = new FormData();
    formData.append('image', imageBlob);
    formData.append('documentId', currentDocumentId);
    formData.append('numReserva', numReserva);
    formData.append('linea', linea);
    formData.append('numOcupante', numOcupante);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BASE_URL}/api/dni_delta/procesar-reverso`,
        formData,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const result = response.data;

      if (result.success) {
        setDocumentoData(result.data);
        setNeedsNextPage(false);
        message.success('Documento procesado correctamente');
        
        if (onSuccess) {
          onSuccess({
            ...result.data,
            numReserva,
            linea,
            numOcupante
          });
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.error || 'Error al procesar el reverso');
    } finally {
      setLoading(false);
    }
  };

  // Subir imagen digital
  const handleUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('Solo se permiten imágenes');
      return false;
    }
    
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('La imagen debe ser menor a 10MB');
      return false;
    }
    
    const url = URL.createObjectURL(file);
    setPreviewImage(url);
    setFrontImage(url);
    procesarDocumento(file);
    
    return false;
  };

  // Reiniciar escaneo
  const resetScan = () => {
    setDocumentoData(null);
    setError(null);
    setNeedsNextPage(false);
    setCurrentDocumentId(null);
    setFrontImage(null);
    setPreviewImage(null);
    
    if (modo === 'fisico') {
      startCamera();
    }
  };

  // Aceptar y guardar
  const handleAccept = () => {
    if (documentoData && onSuccess) {
      onSuccess({
        ...documentoData,
        numReserva,
        linea,
        numOcupante
      });
    }
    onClose();
  };

  // Formatear fecha
  const formatDate = (dateStr) => {
    if (!dateStr) return 'No disponible';
    if (dateStr.includes('/')) return dateStr;
    if (dateStr.length === 8) {
      return `${dateStr.substring(0,2)}/${dateStr.substring(2,4)}/${dateStr.substring(4,8)}`;
    }
    return dateStr;
  };

  // Obtener tipo de documento
  const getTipoDocumento = (tipo) => {
    switch(tipo) {
      case 'D': return { text: 'DNI Español', color: 'blue' };
      case 'P': return { text: 'Pasaporte', color: 'green' };
      case 'N': return { text: 'NIE', color: 'orange' };
      case 'C': return { text: 'Permiso Conducir', color: 'purple' };
      default: return { text: tipo || 'Documento', color: 'default' };
    }
  };

  return (
    <Modal
      title={
        <Space>
          <IdcardOutlined />
          <span>Lector de DNI - {nombreCliente || `Reserva #${numReserva}`}</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={700}
      footer={documentoData ? [
        <Button key="reset" onClick={resetScan}>
          <ReloadOutlined /> Nuevo escaneo
        </Button>,
        <Button key="close" onClick={onClose}>
          Cancelar
        </Button>,
        <Button key="accept" type="primary" onClick={handleAccept}>
          <CheckCircleOutlined /> Aceptar
        </Button>
      ] : [
        <Button key="close" onClick={onClose}>
          Cancelar
        </Button>
      ]}
    >
      <div className="lector-dni-container">
        {!documentoData ? (
          <>
            {/* Selector de modo */}
            <div className="modo-selector">
              <button 
                className={`modo-btn ${modo === 'fisico' ? 'active' : ''}`}
                onClick={() => { setModo('fisico'); resetScan(); }}
              >
                <CameraOutlined /> DNI Físico
              </button>
              <button 
                className={`modo-btn ${modo === 'digital' ? 'active' : ''}`}
                onClick={() => { setModo('digital'); resetScan(); }}
              >
                <UploadOutlined /> DNI Digital
              </button>
            </div>

            {/* Modo físico - Cámara */}
            {modo === 'fisico' && (
              <div className="camera-section">
                {!needsNextPage ? (
                  <>
                    <div className="camera-container">
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        className="camera-preview"
                      />
                      <canvas ref={canvasRef} className="hidden-canvas" />
                      <div className="camera-frame"></div>
                    </div>
                    
                    <Button
                      type="primary"
                      icon={<CameraOutlined />}
                      onClick={capturePhoto}
                      loading={loading}
                      block
                      size="large"
                      style={{ marginTop: 16 }}
                    >
                      Capturar DNI
                    </Button>
                    
                    <Text type="secondary" className="camera-hint">
                      Coloca el DNI dentro del marco y asegura buena iluminación
                    </Text>
                  </>
                ) : (
                  <>
                    <div className="camera-container">
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        className="camera-preview"
                      />
                      <div className="camera-frame"></div>
                    </div>
                    
                    <Alert
                      message="Escanea el reverso"
                      description="Ahora captura el reverso del documento"
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                    
                    <Button
                      type="primary"
                      icon={<CameraOutlined />}
                      onClick={capturePhoto}
                      loading={loading}
                      block
                      size="large"
                    >
                      Capturar Reverso
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* Modo digital - QR */}
            {modo === 'digital' && !documentoData && (
              <div className="upload-section">
                {!qrScanning ? (
                  <>
                    <Button type="primary" icon={<CameraOutlined />} onClick={iniciarEscaneoQR} block>
                      Escanear QR del DNI Digital
                    </Button>
                    <div style={{ textAlign: 'center', marginTop: 16 }}>
                      <Text type="secondary">Enfoca el código QR que aparece en la app Mi Carpeta Ciudadana o en el DNI electrónico</Text>
                    </div>
                  </>
                ) : (
                  <div id="qr-reader" style={{ width: '100%' }}></div>
                )}
              </div>
            )}

            {/* Error */}
            {error && (
              <Alert
                message="Error"
                description={error}
                type="error"
                showIcon
                closable
                onClose={() => setError(null)}
                style={{ marginTop: 16 }}
              />
            )}
          </>
        ) : (
          /* Datos del documento */
          <div className="document-data">
            <div className="document-header">
              <Tag color={getTipoDocumento(documentoData.tipo_documento).color} style={{ fontSize: 16, padding: '4px 12px' }}>
                {getTipoDocumento(documentoData.tipo_documento).text}
              </Tag>
              {documentoData.numero_documento && (
                <Tag color="blue" style={{ fontSize: 16, padding: '4px 12px' }}>
                  Nº: {documentoData.numero_documento}
                </Tag>
              )}
            </div>

            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label={<><UserOutlined /> Nombre completo</>}>
                <Text strong>
                  {documentoData.nombre} {documentoData.apellido1} {documentoData.apellido2 || ''}
                </Text>
              </Descriptions.Item>
              
              {documentoData.fecha_nacimiento && (
                <Descriptions.Item label={<><CalendarOutlined /> Fecha nacimiento</>}>
                  {formatDate(documentoData.fecha_nacimiento)}
                </Descriptions.Item>
              )}
              
              {documentoData.fecha_expedicion && (
                <Descriptions.Item label="Fecha expedición">
                  {formatDate(documentoData.fecha_expedicion)}
                </Descriptions.Item>
              )}
              
              {documentoData.fecha_validez && (
                <Descriptions.Item label="Fecha validez">
                  {formatDate(documentoData.fecha_validez)}
                </Descriptions.Item>
              )}
              
              {documentoData.nacionalidad && (
                <Descriptions.Item label={<><TeamOutlined /> Nacionalidad</>}>
                  {documentoData.nacionalidad}
                </Descriptions.Item>
              )}
              
              {documentoData.sexo && (
                <Descriptions.Item label="Sexo">
                  {documentoData.sexo === 'M' ? 'Masculino' : 'Femenino'}
                </Descriptions.Item>
              )}
              
              {documentoData.domicilio && (
                <Descriptions.Item label={<><EnvironmentOutlined /> Domicilio</>}>
                  {documentoData.domicilio}
                </Descriptions.Item>
              )}
            </Descriptions>

            {documentoData.foto_base64 && (
              <div className="document-photo">
                <Text type="secondary">Foto del documento:</Text>
                <Image 
                  src={`data:image/jpeg;base64,${documentoData.foto_base64}`}
                  alt="Documento"
                  width={120}
                  style={{ borderRadius: 8, marginTop: 8 }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <style jsx>{`
        .lector-dni-container {
          min-height: 400px;
        }
        
        .modo-selector {
          display: flex;
          gap: 12px;
          margin-bottom: 20px;
        }
        
        .modo-btn {
          flex: 1;
          padding: 10px;
          border: 1px solid #d9d9d9;
          background: #fafafa;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        
        .modo-btn:hover {
          border-color: #1890ff;
          color: #1890ff;
        }
        
        .modo-btn.active {
          background: #1890ff;
          border-color: #1890ff;
          color: white;
        }
        
        .camera-section {
          text-align: center;
        }
        
        .camera-container {
          position: relative;
          background: #000;
          border-radius: 12px;
          overflow: hidden;
          aspect-ratio: 4 / 3;
        }
        
        .camera-preview {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .camera-frame {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 85%;
          height: 70%;
          border: 2px solid #1677ff;
          border-radius: 12px;
          pointer-events: none;
          z-index: 10;
        }

        /* Añadir esquinas al marco para mejor visibilidad */
        .camera-frame::before,
        .camera-frame::after {
          content: '';
          position: absolute;
          width: 20px;
          height: 20px;
          border-color: #1677ff;
          border-style: solid;
          border-width: 0;
        }

        /* Esquina superior izquierda */
        .camera-frame::before {
          top: -2px;
          left: -2px;
          border-top-width: 3px;
          border-left-width: 3px;
        }

        /* Esquina inferior derecha */
        .camera-frame::after {
          bottom: -2px;
          right: -2px;
          border-bottom-width: 3px;
          border-right-width: 3px;
        }
        
        .hidden-canvas {
          display: none;
        }
        
        .camera-hint {
          display: block;
          text-align: center;
          margin-top: 12px;
          font-size: 12px;
        }
        
        .upload-section {
          min-height: 300px;
        }
        
        .preview-container {
          position: relative;
          margin-top: 16px;
        }
        
        .preview-spin {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
        
        .document-data {
          max-height: 500px;
          overflow-y: auto;
        }
        
        .document-header {
          display: flex;
          gap: 8px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }
        
        .document-photo {
          margin-top: 16px;
          text-align: center;
          padding-top: 16px;
          border-top: 1px solid #f0f0f0;
        }
      `}</style>
    </Modal>
  );
};

export default LectorDNI;